// Shared budget category color palette for the on-screen top sheet and share bar. Each
// category reads a `hue-*` theme token, so it follows the appearance preset and the
// light/dark mode. Colors are applied via inline styles (not Tailwind arbitrary classes)
// so dynamic indices survive the Tailwind JIT scanner. Printed sheets use the fixed paper
// palette in `print/budgetPrintPalette.ts` instead.

type CategoryHue = { color: string; withAlpha: (alpha: number) => string };

// Each value is written out in full so it reads as a theme variable, never a built string.
const BLUE: CategoryHue = {
  color: "rgb(var(--hue-blue))",
  withAlpha: (alpha) => `rgb(var(--hue-blue) / ${alpha})`,
};
const VIOLET: CategoryHue = {
  color: "rgb(var(--hue-violet))",
  withAlpha: (alpha) => `rgb(var(--hue-violet) / ${alpha})`,
};
const EMERALD: CategoryHue = {
  color: "rgb(var(--hue-emerald))",
  withAlpha: (alpha) => `rgb(var(--hue-emerald) / ${alpha})`,
};
const ROSE: CategoryHue = {
  color: "rgb(var(--hue-rose))",
  withAlpha: (alpha) => `rgb(var(--hue-rose) / ${alpha})`,
};

const CATEGORY_HUES: ReadonlyArray<CategoryHue> = [BLUE, VIOLET, EMERALD, ROSE, BLUE];

// Vivid band used by the share bar and the per-category accent stripe (~48%
// alpha, matching the website's `7a` suffix).
export const BUDGET_BAND_ALPHA = 0.48;

// Calm tint used for full-width grid rows so the share bar carries the color.
export const BUDGET_ROW_TINT_ALPHA = 0.16;

// Hover emphasis for category rows (slightly brighter than their resting tint).
export const BUDGET_ROW_HOVER_ALPHA = 0.24;

// Hover tint for sub-category rows: a lighter, more transparent take on the
// parent category color so children read as a softer echo of the category.
export const BUDGET_SUB_ROW_HOVER_ALPHA = 0.1;

// The contingency row is neutral: a foreground tint shows on light and dark surfaces.
export const CONTINGENCY_BAND_COLOR = "rgb(var(--foreground) / 0.5)";
export const CONTINGENCY_ROW_TINT = "rgb(var(--foreground) / 0.08)";
export const CONTINGENCY_ROW_HOVER_TINT = "rgb(var(--foreground) / 0.12)";
export const CONTINGENCY_SUB_ROW_HOVER_TINT = "rgb(var(--foreground) / 0.06)";

const getCategoryHue = (index: number): CategoryHue =>
  CATEGORY_HUES[((index % CATEGORY_HUES.length) + CATEGORY_HUES.length) % CATEGORY_HUES.length];

export const getBudgetCategoryColor = (index: number): string => getCategoryHue(index).color;

export const budgetCategoryColorWithAlpha = (index: number, alpha: number): string =>
  getCategoryHue(index).withAlpha(alpha);

export const getBudgetBandColor = (colorIndex: number): string =>
  colorIndex < 0
    ? CONTINGENCY_BAND_COLOR
    : budgetCategoryColorWithAlpha(colorIndex, BUDGET_BAND_ALPHA);

export const getBudgetRowTint = (colorIndex: number): string =>
  colorIndex < 0
    ? CONTINGENCY_ROW_TINT
    : budgetCategoryColorWithAlpha(colorIndex, BUDGET_ROW_TINT_ALPHA);

export const getBudgetRowHoverTint = (colorIndex: number): string =>
  colorIndex < 0
    ? CONTINGENCY_ROW_HOVER_TINT
    : budgetCategoryColorWithAlpha(colorIndex, BUDGET_ROW_HOVER_ALPHA);

export const getBudgetSubRowHoverTint = (colorIndex: number): string =>
  colorIndex < 0
    ? CONTINGENCY_SUB_ROW_HOVER_TINT
    : budgetCategoryColorWithAlpha(colorIndex, BUDGET_SUB_ROW_HOVER_ALPHA);
